import * as React from 'react';
import { RouterState, withRouter } from 'react-router';
import { DataShape, SubmissionError } from 'redux-form';
import { diagnostics } from 'ui-common';
import * as queryString from 'query-string';

import InputMap from 'login-components/Common/Inputs/InputMap';
import ErrorMessage from 'login-components/Common/Notifications/ErrorMessage/ErrorMessage';
import { setHref } from 'login-utils/globalWrappers';
import connectedSimpleForm from 'login-components/SimpleForm/SimpleForm';
import { get, login } from 'login-apis/thirdPartyRecipientsAPI';

type Props = RouterState;

enum GetRequestState {
  Requesting,
  Success,
  Error,
}

enum ProvideEconsentStatus {
  Success = 'SUCCESS',
  Failure = 'FAILURE',
}

interface State {
  email: string | null;
  verificationHint: string | null;
  getRequestState: GetRequestState;
}

class UserReadableError extends Error {
  // Ignore coverage to account for work around a bug:
  // https://github.com/gotwarlost/istanbul/issues/690
  // istanbul ignore next
  constructor(message: string) {
    super(message);
    Object.setPrototypeOf(this, UserReadableError.prototype);
  }
}

// tslint:disable-next-line:max-classes-per-file
export class ProvideEconsent extends React.Component<Props, State> {
  state = {
    email: null,
    verificationHint: null,
    getRequestState: GetRequestState.Requesting,
  };

  private parseQueryStringParams() {
    const { location } = this.props;
    const { tprId } = queryString.parse(location.search) as { [key: string]: string };

    if (!tprId) {
      throw new Error(`Required query string parameters for Provide Econsent page are missing. tprId: ${tprId}.`);
    }

    return { tprId };
  }

  async componentDidMount() {
    try {
      const { tprId } = this.parseQueryStringParams();
      const { data } = await get(tprId);
      const { email, verificationHint } = data;
      this.setState({
        email,
        verificationHint,
        getRequestState: GetRequestState.Success,
      });
    } catch (e) {
      diagnostics.error(e);
      this.setState({ getRequestState: GetRequestState.Error });
    }
  }

  private async onSubmit(values: DataShape) {
    try {
      const { verificationCode, acceptTermsOfServiceAndEConsent } = values;
      const { tprId } = this.parseQueryStringParams();

      const { data } = await login({
        id: tprId,
        verificationCode,
        econsentProvided: acceptTermsOfServiceAndEConsent,
      });

      const { status, errorMessage } = data;

      if (status === ProvideEconsentStatus.Failure) {
        throw new UserReadableError(errorMessage);
      }

      setHref('/#/review-documents-completed');
    } catch (e) {
      if (e instanceof UserReadableError) {
        throw new SubmissionError({ _error: e.message });
      }

      diagnostics.error(e);
      throw new SubmissionError({
        _error: 'We experienced a problem logging you in. Please contact your loan team.',
      });
    }
  }

  render() {
    const { getRequestState, email, verificationHint } = this.state;
    const defaultVerificationHint = 'Please enter the verification code provided by your loan team.';

    if (getRequestState === GetRequestState.Error) {
      return <ErrorMessage errorText="Unable to login." />;
    }

    if (getRequestState === GetRequestState.Requesting) {
      return <div />;
    }

    const inputs: FormInput[] = [
      {
        ...InputMap.EmailInput,
        identifier: 'email address',
        name: 'email',
        disabled: true,
        label: 'Email address',
        hidePlaceholder: true,
      },
      {
        ...InputMap.SimpleTextInput,
        identifier: 'response',
        label: verificationHint || defaultVerificationHint,
        name: 'verificationCode',
      },
      {
        ...InputMap.RequirementInput,
        acceptedTermsTypes: ['termsOfUse', 'privacyPolicy', 'eConsent'],
        identifier: 'termsAndEConsent',
        name: 'acceptTermsOfServiceAndEConsent',
      },
    ];
    const SimpleForm = connectedSimpleForm(inputs, { email });

    return <SimpleForm inputs={inputs} submitFn={values => this.onSubmit(values)} actionText="Login" />;
  }
}

export default withRouter(ProvideEconsent);
